import { useCallback, useContext, useMemo } from 'react';
import { DisplayNamesRegion, Locale } from '@medable/i18n-core-v4';
import { LocaleContext } from './i18n_context';
/**
 * Returns narrow, localized display names for region codes.
 *
 * Pseudo-locales use a code-preserving placeholder because `DisplayNamesRegion`
 * does not pseudo-localize region names itself.
 *
 * Falls back to the raw code when the value is not a well-formed region subtag.
 * Callers pass values that are region-shaped but not ISO regions - `MOMRegion`
 * carries a non-geographic `local` next to `cn`/`eu`/`us` - and the underlying
 * `Intl.DisplayNames` throws `RangeError` on those, which is enough to take down
 * the calling component tree. Intl already returns the code itself for a
 * well-formed but unknown region, so this keeps that behaviour consistent.
 */
export const useRegionDisplayName = () => {
    const locale = useContext(LocaleContext);
    const localeInstance = useMemo(() => new Locale(locale), [locale]);
    const displayNames = useMemo(() => new DisplayNamesRegion(localeInstance, { style: 'narrow' }), [localeInstance]);
    return useCallback((region) => {
        const regionCode = region === null || region === void 0 ? void 0 : region.trim().toUpperCase();
        if (!regionCode) {
            return '';
        }
        if (localeInstance.isPseudoLocale) {
            return `[[[Region:${regionCode}]]]`;
        }
        try {
            return displayNames.of(regionCode);
        }
        catch (_a) {
            return regionCode;
        }
    }, [displayNames, localeInstance]);
};
//# sourceMappingURL=use_region_display_name.js.map